import ExcelJS from 'exceljs';
import FileSaver from 'file-saver';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import moment from 'moment';

export const EXCEL_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

function createWorkbook() {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'CytoScriba';
  workbook.lastModifiedBy = 'CytoScriba';
  workbook.created = new Date();
  workbook.modified = new Date();
  return workbook;
}

function fixHeadersStyles(worksheet) {
  for (let col = 1; col <= worksheet.columns.length; col++) {
    const cell = worksheet.getCell(1, col);
    cell.style = {
      font: {
        bold: true,
      },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'ffd4dfff' },
      },
      border: {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      },
    };
  }
}

function buildReceiptsWorksheetColumns(worksheet) {
  worksheet.columns = [
    { header: 'User', key: 'user', width: 20 },
    { header: 'Status', key: 'status', width: 10 },
    { header: 'Upload Date', key: 'created', width: 15 },
    { header: 'Receipt Date', key: 'date', width: 15 },
    { header: 'Amount', key: 'amount', width: 10 },
    { header: 'Kind', key: 'kind', width: 10 },
    { header: 'Receipt Photo', key: 'photo', width: 12 },
  ];
  fixHeadersStyles(worksheet);
}

function buildTotalsWorksheetColumns(worksheet) {
  worksheet.columns = [
    { header: 'User', key: 'user', width: 20 },
    { header: 'Other', key: 'other', width: 10 },
    { header: 'Parking', key: 'park', width: 10 },
    { header: 'Sport', key: 'sport', width: 10 },
    { header: 'Total', key: 'total', width: 10 },
  ];
  fixHeadersStyles(worksheet);
}

export function getKindName(kind) {
  switch (kind) {
    case 'sport':
      return 'Sport';
    case 'park':
      return 'Parking';
  }
  return 'Other';
}

function parseDate(date) {
  const m = moment(date);
  // fix time offset
  return m.add(m.utcOffset(), 'm').toDate();
}

function getReceiptPhotoUrl(storage, receipt) {
  if (receipt.photoPath) {
  }
  return getDownloadURL(ref(storage, receipt.photoPath));
}

function fixRowStyle(row) {
  row.eachCell(cell =>
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    });
}

async function buildReceiptsWorksheetRows(worksheet, receipts) {
  const storage = getStorage();
  const photos = await Promise.all(receipts.map(receipt => receipt.photoPath && getDownloadURL(ref(storage, receipt.photoPath))));
  for (let i = 0; i < receipts.length; i++) {
    const receipt = receipts[i];
    let photoUrl = photos[i];
    const row = worksheet.addRow({
      user: receipt.user,
      status: receipt.Status,
      created: parseDate(receipt.created),
      date: parseDate(receipt.date),
      amount: parseFloat(receipt.amount),
      kind: getKindName(receipt.kind),
      photo: photoUrl && {
        text: 'Receipt Photo',
        hyperlink: photoUrl,
        tooltip: 'Click to view receipt photo',
      },
    });
    fixRowStyle(row);
    row.getCell('photo').style.font = {
      underline: true,
      color: { argb: 'FF0000FF' },
    };
    row.getCell('amount').numFmt = '₪ 0.00';
    row.getCell('created').numFmt = 'ddd, dd/mm/yyyy';
    row.getCell('date').numFmt = 'ddd, dd/mm/yyyy';
  }
}

async function buildTotalsWorksheetRows(worksheet, receipts) {
  const totals = {};
  for (const receipt of receipts) {
    let kinds = totals[receipt.user];
    if (!kinds) {
      kinds = {};
      totals[receipt.user] = kinds;
    }
    const kind = receipt.kind || 'other';
    kinds[kind] = (kinds[kind] || 0) + +receipt.amount;
  }
  Object.entries(totals).forEach(([user, totals]) => {
    let other = totals.other || 0;
    let park = totals.park || 0;
    let sport = totals.sport || 0;
    const row = worksheet.addRow({
      user,
      other,
      park,
      sport,
      total: other + park + sport,
    });
    fixRowStyle(row);
    row.eachCell(cell => cell.numFmt = '₪ 0.00');
    row.getCell('total').style.font = { bold: true };
  });
}

function createWorksheet(workbook, name) {
  const worksheet = workbook.addWorksheet(name);
  worksheet.views = [
    { state: 'frozen', xSplit: 1, ySplit: 1, activeCell: 'A1' },
  ];
  return worksheet;
}

export async function downloadReceiptsExcel(receipts) {
  const workbook = createWorkbook();
  let worksheet = createWorksheet(workbook, 'Totals');
  buildTotalsWorksheetColumns(worksheet);
  await buildTotalsWorksheetRows(worksheet, receipts);

  worksheet = createWorksheet(workbook, 'Receipts');
  buildReceiptsWorksheetColumns(worksheet);
  await buildReceiptsWorksheetRows(worksheet, receipts);

  // download file
  const buffer = await workbook.xlsx.writeBuffer();
  const rawData = new Blob([buffer], { type: EXCEL_CONTENT_TYPE });
  FileSaver.saveAs(rawData, `CytoScriba-Receipt-${Date.now()}.xlsx`);
}
